/**
 * The axis a gantt draws, and where each row's bar sits on it.
 *
 * Pure, and the whole of what makes a gantt a gantt: everything else is the row
 * window it shares with the grid, the gallery and the list.
 *
 * **The axis does not come from the rows.** The MCP App's gantt shell derives
 * its span from the page it was handed, which is right there -- it has one page
 * and no way to ask for another. Here the rows arrive a window at a time, so a
 * span read off them would move every time somebody scrolled, and every bar
 * would slide under the reader. The axis is a *range the reader is looking at*
 * instead, moved a screen at a time, and the `timescale` the view stores is what
 * one column of it covers.
 *
 * All of it in UTC -- `Date.UTC`, `getUTC*` -- for the reason the schedule's
 * calendar gives: these are calendar units, and doing the arithmetic in one
 * fixed frame is what stops "the 1st" from being two different days depending on
 * where the browser is.
 */
import type { ViewOf } from '@/api/types'

/**
 * Read off the view rather than named again here, so a fifth timescale on the
 * backend is a compile error in `TIMESCALE_COLUMNS` rather than a chart drawn
 * at whatever the last case happened to be.
 */
export type Timescale = NonNullable<ViewOf<'gantt'>['timescale']>

/**
 * How many columns of each timescale are shown at once.
 *
 * Chosen so a screenful is a period somebody would name: a month of days, half
 * a year of weeks, a year of months, a decade of years. A fixed count rather
 * than one measured from the container, because the column width follows from
 * it -- and because a chart whose number of columns changed with the window
 * would renumber its axis every time the sidebar was collapsed.
 */
export const TIMESCALE_COLUMNS: Record<Timescale, number> = {
  day: 30,
  week: 26,
  month: 12,
  year: 10,
}

/** The start of the unit a date falls in: midnight, Monday, the 1st, January. */
export function startOfUnit(date: Date, timescale: Timescale): Date {
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()

  switch (timescale) {
    case 'day':
      return new Date(Date.UTC(year, month, day))
    case 'week': {
      // Monday, which is the week the rest of this application starts on.
      const midnight = new Date(Date.UTC(year, month, day))
      const back = (midnight.getUTCDay() + 6) % 7
      midnight.setUTCDate(midnight.getUTCDate() - back)
      return midnight
    }
    case 'month':
      return new Date(Date.UTC(year, month, 1))
    case 'year':
    default:
      return new Date(Date.UTC(year, 0, 1))
  }
}

/** The same instant moved by whole units, which is not the same as adding days. */
export function addUnits(date: Date, timescale: Timescale, count: number): Date {
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()

  switch (timescale) {
    case 'day':
      return new Date(Date.UTC(year, month, day + count))
    case 'week':
      return new Date(Date.UTC(year, month, day + count * 7))
    case 'month':
      // `Date.UTC` normalises an out-of-range month, so December plus two is
      // February of the next year without any arithmetic here.
      return new Date(Date.UTC(year, month + count, day))
    case 'year':
    default:
      return new Date(Date.UTC(year + count, month, day))
  }
}

/** The instants each column of the axis begins at, from the anchor forward. */
export function timelineColumns(anchor: Date, timescale: Timescale): Date[] {
  const first = startOfUnit(anchor, timescale)
  return Array.from({ length: TIMESCALE_COLUMNS[timescale] }, (_, index) =>
    addUnits(first, timescale, index),
  )
}

export interface TimelineRange {
  from: Date
  /** Exclusive: the start of the column after the last one drawn. */
  to: Date
}

export function timelineRange(anchor: Date, timescale: Timescale): TimelineRange {
  const first = startOfUnit(anchor, timescale)
  return {
    from: first,
    to: addUnits(first, timescale, TIMESCALE_COLUMNS[timescale]),
  }
}

export interface Bar {
  /** Where the bar starts and ends, as fractions of the visible range. */
  from: number
  to: number
  /** Whether the row's span runs past the edge of what is being shown. */
  continuesBefore: boolean
  continuesAfter: boolean
}

/**
 * One row's bar, clipped to the range, or null if it is not in it at all.
 *
 * A row whose dates are the wrong way round is drawn between them rather than
 * refused: the backend stores whatever was typed, and a bar is a better answer
 * than nothing about a row somebody can see is there.
 *
 * The clipping is reported rather than hidden. A bar that stops flush at the
 * edge of the chart looks like a task that ends there, which is a different
 * statement from "this runs on past what you are looking at".
 */
export function barFor(
  start: Date | null,
  end: Date | null,
  range: TimelineRange,
): Bar | null {
  if (start === null || end === null) return null

  const first = Math.min(start.getTime(), end.getTime())
  const last = Math.max(start.getTime(), end.getTime())
  const from = range.from.getTime()
  const to = range.to.getTime()

  // Half open at the far end, matching the range itself: a task beginning
  // exactly where the chart stops is not in it.
  if (last < from || first >= to) return null

  const span = to - from
  return {
    from: Math.max(0, (first - from) / span),
    to: Math.min(1, (last - from) / span),
    continuesBefore: first < from,
    continuesAfter: last > to,
  }
}

/** What a column of the axis is called, at each timescale. */
export function columnLabel(column: Date, timescale: Timescale): string {
  const format: Intl.DateTimeFormatOptions =
    timescale === 'year'
      ? { year: 'numeric' }
      : timescale === 'month'
        ? { month: 'short' }
        : { month: 'short', day: 'numeric' }
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...format }).format(column)
}
